import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Container, Spinner } from 'react-bootstrap';
import { marketIntegrationService } from '../../lib/api/marketplaceIntegration';
import Turnstile from 'react-turnstile';
import { cloudflareSiteKey } from '../../config/constants';
import InstallationSuccessfulImage from '../../assets/img/keka/installation-successful.png'
import InstallationSuccessfulWithStepsImage from '../../assets/img/keka/Installation-steps.png';
import SomethingWentWrongImage from '../../assets/img/keka/something-wentwrong.png';
import unauthorizedImage from '../../assets/img/keka/unautorised.png';

interface KekaResponseData {
    companyExisted: boolean;
    message: string;
    succeeded: boolean;
}

interface KekaApiResponse {
    isSuccessful: boolean;
    error: string | null;
    resultObject: KekaResponseData;
}

export default function Keka() {
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [verified, setVerified] = useState(false);
    const [token, setToken] = useState<string>('');
    const [status, setStatus] = useState<'success' | 'error' | 'success-new' | 'unauthorized' | null>(null);
    const [message, setMessage] = useState({ title: '', description: '' });

    const handleTurnstileVerify = async (turnstileToken: string) => {
        setVerifying(true);
        try {
            const data = await marketIntegrationService.verifyCloudflareToken(turnstileToken);
            if (data?.isSuccessful && data.resultObject.success) {
                setToken(turnstileToken);
                setVerified(true);
            } else {
                setVerified(false);
                setStatus('unauthorized');
                setMessage({
                    title: 'Unauthorized',
                    description: 'Verification failed. Please refresh the page and try again.',
                });
            }
        } catch (error) {
            console.error('Turnstile verification failed:', error);
            setVerified(false);
            setStatus('unauthorized');
            setMessage({
                title: 'Unauthorized',
                description: 'Verification failed. Please refresh the page and try again.',
            });
        } finally {
            setVerifying(false);
        }
    };

    useEffect(() => {
        if (!verified || !token) return;

        const installKekaWorkflow = async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams(location.search);
                const code = params.get('code');

                if (!code) {
                    setStatus('error');
                    setMessage({
                        title: 'Missing Authorization Code',
                        description:
                            'Authorization code is missing from the URL. Please try the integration process again.',
                    });
                    setLoading(false);
                    return;
                }
                const apiResponse = (await marketIntegrationService.installKekaWorkflow(
                    code,
                    token
                )) as KekaApiResponse;

                if (apiResponse?.isSuccessful && apiResponse?.resultObject) {
                    const { succeeded, companyExisted } = apiResponse.resultObject;

                    if (succeeded && companyExisted) {
                        setStatus('success');
                        setMessage({
                            title: 'Installation Successful',
                            description: 'You can now track candidates with offerX',
                        });
                    } else if (succeeded && !companyExisted) {
                        setStatus('success-new');
                        setMessage({
                            title: 'Installation Successful',
                            description: 'You can now track candidates with offerX',
                        });
                    } else {
                        setStatus('error');
                        setMessage({
                            title: 'Something Went Wrong',
                            description: 'Please contact OfferX support',
                        });
                    }
                } else {
                    setStatus('error');
                    setMessage({
                        title: 'Something Went Wrong',
                        description: 'Please contact OfferX support',
                    });
                }
            } catch (err: any) {
                console.error('Keka integration error:', err);
                setStatus('error');
                setMessage({
                    title: 'Something Went Wrong',
                    description: 'Please contact OfferX support',
                });
            } finally {
                setLoading(false);
            }
        };

        installKekaWorkflow();
    }, [verified, token, location.search]);

    if (!verified && status !== 'unauthorized') {
        return (
            <Container
                fluid
                className="d-flex flex-column align-items-center justify-content-center min-vh-100 bg-light"
            >
                <p className="text-secondary mb-3 fs-5 fw-medium">Please verify you're human:</p>

                <Turnstile sitekey={cloudflareSiteKey} onVerify={handleTurnstileVerify} size="normal" theme="light" />

                {verifying && (
                    <div className="mt-3">
                        <Spinner animation="border" size="sm" variant="primary" />
                        <span className="text-muted ms-2 small">Verifying...</span>
                    </div>
                )}

                <p className="text-muted mt-3 small">Verification required to continue.</p>
            </Container>
        );
    }

    if (loading) {
        return (
            <Container fluid className="d-flex align-items-center justify-content-center min-vh-100 bg-light">
                <div className="text-center">
                    <Spinner animation="border" variant="primary" style={{ width: '4rem', height: '4rem' }} />
                    <h5 className="mt-4 text-secondary">Setting up Keka integration...</h5>
                    <p className="text-muted">Please wait while we connect your account</p>
                </div>
            </Container>
        );
    }

    if (status === 'success') {
        return (
            <Container
                fluid
                className="d-flex align-items-center justify-content-center min-vh-100"
                style={{ backgroundColor: '#EAF6FF' }}
            >
                <div className="text-center" style={{ width: '100%' }}>
                    <img
                        src={InstallationSuccessfulImage}
                        alt="Installation Successful"
                        style={{ width: '60%', height: '40%' }}
                    />
                </div>
            </Container>
        );
    }

    if (status === 'success-new') {
        return (
            <Container
                fluid
                className="d-flex align-items-center justify-content-center min-vh-100 bg-light"
                style={{ backgroundColor: '#EAF6FF' }}
            >
                <div className="text-center" style={{ width: '100%' }}>
                    <img
                        src={InstallationSuccessfulWithStepsImage}
                        alt="Installation Successful"
                        style={{ width: '60%', height: '40%' }}
                    />
                </div>
            </Container>
        );
    }

    if (status === 'unauthorized') {
        return (
            <Container
                fluid
                className="d-flex align-items-center justify-content-center min-vh-100"
                style={{ backgroundColor: '#EAF6FF' }}
            >
                <div className="text-center" style={{ width: '100%' }}>
                    <img src={unauthorizedImage} alt="Unauthorised Access" style={{ width: '60%', height: '40%' }} />
                </div>
            </Container>
        );
    }
    return (
        <Container
            fluid
            className="d-flex align-items-center justify-content-center min-vh-100 bg-light"
            style={{ backgroundColor: '#EAF6FF' }}
        >
            <div className="text-center" style={{ width: '100%' }}>
                <img src={SomethingWentWrongImage} alt="Something Went Wrong" style={{ width: '60%', height: '40%' }} />
            </div>
        </Container>
    );
}
